import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import WhatsAppMedia from './WhatsAppMedia';
import { reactionBadge, bubbleTime } from './threadMessages';
import { QUICK_REACTIONS, wa } from './waTheme';

function deliveryKey(ticks, status) {
  return String(ticks || status || '').toLowerCase();
}

function deliveryWord(key) {
  if (key === 'read') return 'Read';
  if (key === 'delivered') return 'Delivered';
  if (key === 'failed') return 'Failed';
  if (key === 'queued') return 'Sending';
  if (key === 'sent' || key === 'accepted') return 'Sent';
  return '';
}

function deliveryColor(key) {
  if (key === 'failed') return wa.fail;
  if (key === 'read') return wa.tickRead;
  if (key === 'delivered') return wa.green;
  return wa.meta;
}

const FAILURE_BY_CODE = {
  131008: 'A required template field is missing',
  131009: 'A template field is invalid',
  131026: 'WhatsApp could not deliver this message',
  131031: 'The WhatsApp account is locked',
  131042: 'WhatsApp billing is blocking sends',
  131047: 'The 24-hour reply window was closed',
  131049: 'WhatsApp held this marketing message back',
  131050: 'This parent stopped marketing messages',
  131051: 'WhatsApp does not support this message type',
  132000: 'Template fields do not match the approved template',
  132001: 'This template is not available on WhatsApp',
  132012: 'A template field is in the wrong format',
  132015: 'This template is paused',
  132016: 'This template is disabled',
};

function mediaNoun(message) {
  const type = String(message?.type || '').toLowerCase();
  const mime = String(message?.media?.mime_type || '').toLowerCase();
  if (type === 'video' || mime.startsWith('video/')) return 'video';
  if (type === 'image' || mime.startsWith('image/')) return 'image';
  if (type === 'audio' || mime.startsWith('audio/')) return 'audio';
  if (type === 'document' || mime.startsWith('application/')) return 'file';
  return 'media';
}

function errorRaw(error) {
  if (error == null || error === '') return '';
  if (typeof error === 'object') {
    return [error.code || error.error_code, error.message || error.error].filter(Boolean).join(' ').trim();
  }
  const text = String(error).trim();
  if (!text.startsWith('{')) return text;
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') {
      const joined = [parsed.code || parsed.error_code, parsed.message || parsed.error].filter(Boolean).join(' ').trim();
      return joined || text;
    }
  } catch {
    /* stored as plain text */
  }
  return text;
}

function failureDetail(error, message) {
  const raw = errorRaw(error);
  if (!raw || raw.toLowerCase() === 'failed') return '';
  const code = (raw.match(/\b(1\d{5})\b/) || [])[1];
  if (code === '131053') return `${code} — WhatsApp could not upload the ${mediaNoun(message)}`;
  if (code && FAILURE_BY_CODE[code]) return `${code} — ${FAILURE_BY_CODE[code]}`;
  return raw;
}

function senderFirst(name) {
  return String(name || '').trim().split(/\s+/).filter(Boolean)[0] || '';
}

export default function WhatsAppBubble({
  message,
  tz,
  windowOpen,
  reacting,
  onReact,
  onMediaExpired,
}) {
  const mine = message.direction === 'out';
  const visual = Boolean(message.media) && (
    ['image', 'video'].includes(String(message.type || '').toLowerCase())
    || String(message.media?.mime_type || '').startsWith('image/')
    || String(message.media?.mime_type || '').startsWith('video/')
  );
  const badges = reactionBadge(message.reactions);
  const canReact = windowOpen && Boolean(message.bp_message_id) && message.type !== 'reaction';
  const mineReaction = (message.reactions || []).find((item) => item.direction === 'out')?.emoji;
  const delivery = deliveryKey(message.ticks, message.status);
  const statusWord = deliveryWord(delivery);
  const failure = delivery === 'failed' ? failureDetail(message.error, message) : '';
  const statusLead = [senderFirst(message.sent_by_name), bubbleTime(message.occurred_at, tz)].filter(Boolean).join(' · ');

  return (
    <Box
      className="wa-row"
      sx={{
        display: 'flex',
        justifyContent: mine ? 'flex-end' : 'flex-start',
        mb: badges.length ? 2.25 : 0.6,
        px: 0.5,
      }}
    >
      <Box sx={{ position: 'relative', maxWidth: '75%', '&:hover .wa-react': { opacity: 1, pointerEvents: 'auto' } }}>
        {canReact && (
          <Box
            className="wa-react"
            sx={{
              opacity: 0,
              pointerEvents: 'none',
              transition: 'opacity .12s ease',
              position: 'absolute',
              bottom: 'calc(100% - 8px)',
              pb: '8px',
              ...(mine ? { right: 0 } : { left: 0 }),
              zIndex: 2,
              '@media (hover: none)': { opacity: 1, pointerEvents: 'auto', position: 'static', pb: 0, mb: 0.5 },
            }}
          >
            <Box sx={{ display: 'flex', bgcolor: '#fff', borderRadius: 999, boxShadow: '0 1px 4px rgba(11,20,26,.18)', px: 0.5 }}>
              {QUICK_REACTIONS.map((emoji) => (
                <IconButton
                  key={emoji}
                  aria-label={`React ${emoji}`}
                  disabled={reacting}
                  onClick={() => onReact(message, mineReaction === emoji ? '' : emoji)}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 18,
                    bgcolor: mineReaction === emoji ? '#e7fce3' : 'transparent',
                  }}
                >
                  {emoji}
                </IconButton>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            px: visual ? 0.5 : 1,
            pt: visual ? 0.5 : 0.6,
            pb: 0.4,
            borderRadius: mine ? '8px 0 8px 8px' : '0 8px 8px 8px',
            bgcolor: mine ? wa.bubbleOut : wa.bubbleIn,
            boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
            color: wa.text,
          }}
        >
          {message.campaign_key && (
            <Typography sx={{ fontSize: 11, color: wa.green, px: visual ? 0.75 : 0.25, pt: 0.25 }}>
              {message.campaign_key}
            </Typography>
          )}
          {message.media && (
            <WhatsAppMedia
              media={message.media}
              messageType={message.type}
              alt={message.text}
              onExpired={onMediaExpired}
            />
          )}
          {message.text && (
            <Typography sx={{ fontSize: 14.2, lineHeight: 1.35, whiteSpace: 'pre-wrap', wordBreak: 'break-word', px: visual ? 0.75 : 0.4, pt: 0.35 }}>
              {message.text}
            </Typography>
          )}
          {message.handled_as === 'stop' && (
            <Typography sx={{ fontSize: 12, color: wa.fail, px: 0.4 }}>
              STOP — parent opted out of campaigns
            </Typography>
          )}
          {!mine && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, px: 0.4, pt: 0.15 }}>
              <Typography sx={{ fontSize: 11, color: wa.meta, lineHeight: 1 }}>
                {bubbleTime(message.occurred_at, tz)}
              </Typography>
            </Box>
          )}
        </Box>
        {badges.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -14,
              ...(mine ? { right: 8 } : { left: 8 }),
              bgcolor: '#fff',
              border: '1px solid #e9edef',
              borderRadius: 999,
              boxShadow: '0 1px 1px rgba(11,20,26,.12)',
              px: 0.7,
              py: 0.15,
              fontSize: 14,
              lineHeight: 1.3,
              display: 'flex',
              gap: 0.4,
            }}
          >
            {badges.map(([emoji, count]) => (
              <span key={emoji}>{count > 1 ? `${emoji} ${count}` : emoji}</span>
            ))}
          </Box>
        )}
        </Box>
        {mine && (statusLead || statusWord) && (
          <Typography sx={{ fontSize: 11, color: wa.meta, textAlign: 'right', lineHeight: 1.35, mt: badges.length ? 1.75 : 0.35, pr: 0.25 }}>
            {statusLead}
            {statusWord && (
              <>
                {statusLead ? ' · ' : ''}
                <Box
                  component="span"
                  sx={{
                    color: deliveryColor(delivery),
                    fontWeight: delivery === 'sent' || delivery === 'accepted' || delivery === 'queued' ? 400 : 600,
                  }}
                >
                  {statusWord}
                </Box>
              </>
            )}
            {failure && (
              <Box component="span" sx={{ color: wa.fail, fontWeight: 500 }}>
                {` · ${failure}`}
              </Box>
            )}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
